import dayjs from "dayjs";
import {
  INVITE_APPOINTMENT_IDENTIFIER_SYSTEM,
  INVITE_RESPONSE_TASK_CODE,
  INVITE_RESPONSE_TEXT,
  INVITE_TASK_CODE_SYSTEM,
  type InviteResponse,
} from "../constants";
import { inviteAppointmentId, invitePatientDisplay } from "../helpers/inviteTaskHelpers";

function responseDescription(params: {
  guestName: string;
  response: InviteResponse;
  patientName: string;
  appointmentStart?: string;
}) {
  const start = params.appointmentStart ? dayjs(params.appointmentStart) : undefined;
  const when = start?.isValid() ? ` · ${start.format("DD/MM HH:mm")}` : "";
  const verb = params.response === "accepted" ? "aceitou" : "recusou";
  return `${params.guestName} ${verb} o convite de teleconsultoria de ${params.patientName}${when}`;
}

/**
 * Notificação da resposta ao convite, destinada a QUEM CONVIDOU.
 * - `for`/`owner` = PractitionerRole de quem convidou (requester do convite);
 * - `requester` = PractitionerRole do convidado (for do convite);
 * - Appointment só como `identifier` (fica em outro servidor) e a mesma Location.
 */
export function buildInviteResponseTask(params: {
  inviteTask: fhir5.Task;
  response: InviteResponse;
  appointmentStart?: string;
}): fhir5.Task | undefined {
  const { inviteTask, response } = params;
  const hostRole = inviteTask.requester;
  const guestRole = inviteTask.for;
  if (!hostRole?.reference || !guestRole?.reference) return undefined;

  const now = dayjs().toISOString();
  const appointmentId = inviteAppointmentId(inviteTask);
  return {
    resourceType: "Task",
    ...(appointmentId && {
      identifier: [{ system: INVITE_APPOINTMENT_IDENTIFIER_SYSTEM, value: appointmentId }],
    }),
    status: "requested",
    intent: "order",
    priority: "routine",
    code: {
      coding: [{ system: INVITE_TASK_CODE_SYSTEM, code: INVITE_RESPONSE_TASK_CODE }],
      text: INVITE_RESPONSE_TEXT[response],
    },
    description: responseDescription({
      guestName: guestRole.display || "O teleconsultor",
      response,
      patientName: invitePatientDisplay(inviteTask),
      appointmentStart: params.appointmentStart,
    }),
    ...(inviteTask.location?.reference && { location: inviteTask.location }),
    for: { reference: hostRole.reference, display: hostRole.display },
    owner: { reference: hostRole.reference, display: hostRole.display },
    requester: { reference: guestRole.reference, display: guestRole.display },
    authoredOn: now,
    lastModified: now,
  };
}
